// Task Cards — Motion Cards: Creative Projects & Composition
export const CREATIVE = [
  {
    id: "rule-of-thirds",
    photo: "/taskcards/rule-of-thirds.jpg",
    icon: "GridFour",
    title: { en: "Rule of Thirds", es: "Regla de los Tercios" },
    tagline: { en: "Place subjects off-center for natural, balanced frames.", es: "Coloca los sujetos descentrados para encuadres naturales y equilibrados." },
    tips: {
      en: [
        "Imagine a 3×3 grid; put key elements on the lines or their intersections.",
        "Place eyes on the upper third line in portraits.",
        "Put the horizon on the top or bottom third, not the middle.",
        "Turn on your camera's grid overlay to train your eye.",
        "Break the rule deliberately for symmetry or tension.",
      ],
      es: [
        "Imagina una rejilla de 3×3; pon lo importante en las lineas o sus cruces.",
        "Coloca los ojos en la linea del tercio superior en retratos.",
        "Pon el horizonte en el tercio superior o inferior, no en el medio.",
        "Activa la cuadricula de tu camara para entrenar el ojo.",
        "Rompe la regla a proposito para simetria o tension.",
      ],
    },
    remember: { en: "Off-center beats dead-center.", es: "Descentrado supera al centro muerto." },
  },
  {
    id: "leading-lines",
    photo: "/taskcards/leading-lines.jpg",
    icon: "ArrowsHorizontal",
    title: { en: "Leading Lines", es: "Lineas Guia" },
    tagline: { en: "Use lines to pull the viewer's eye to your subject.", es: "Usa lineas para llevar la mirada hacia tu sujeto." },
    tips: {
      en: [
        "Roads, fences, rivers, shadows and railings all guide the eye.",
        "Start lines from a corner to lead deep into the scene.",
        "Converging lines create powerful depth and perspective.",
        "Lead toward your subject, not away from it or out of frame.",
        "Get low or high to make lines more dramatic.",
      ],
      es: [
        "Caminos, vallas, rios, sombras y barandales guian la mirada.",
        "Inicia las lineas desde una esquina para llevar al fondo de la escena.",
        "Las lineas convergentes crean profundidad y perspectiva potentes.",
        "Guia hacia tu sujeto, no lejos de el ni fuera del encuadre.",
        "Ponte bajo o alto para hacer las lineas mas dramaticas.",
      ],
    },
    remember: { en: "Lines should lead to the subject.", es: "Las lineas deben llevar al sujeto." },
  },
  {
    id: "minimalism",
    photo: "/taskcards/minimalism.jpg",
    icon: "Square",
    title: { en: "Minimalism", es: "Minimalismo" },
    tagline: { en: "Less is more — one subject, lots of clean space.", es: "Menos es mas — un sujeto, mucho espacio limpio." },
    tips: {
      en: [
        "Pick one clear subject and remove everything else.",
        "Use large areas of negative space (sky, wall, water).",
        "Limit your palette to one or two colors.",
        "Strong, simple shapes and clean backgrounds read instantly.",
        "Place the subject small and off-center for impact.",
      ],
      es: [
        "Elige un solo sujeto claro y quita todo lo demas.",
        "Usa grandes areas de espacio negativo (cielo, pared, agua).",
        "Limita tu paleta a uno o dos colores.",
        "Formas simples y fuertes con fondos limpios se leen al instante.",
        "Coloca el sujeto pequeno y descentrado para mayor impacto.",
      ],
    },
    remember: { en: "One subject, lots of space, few colors.", es: "Un sujeto, mucho espacio, pocos colores." },
  },
  {
    id: "blue-hour",
    photo: "/taskcards/blue-hour.jpg",
    icon: "CloudMoon",
    title: { en: "Blue Hour Magic", es: "Magia de la Hora Azul" },
    tagline: { en: "The deep-blue window just before sunrise / after sunset.", es: "La ventana azul intensa justo antes del amanecer / tras el atardecer." },
    settings: [
      { k: "aperture", v: "f/8" },
      { k: "shutter", v: "1s–10s (tripod)" },
      { k: "iso", v: "100–400" },
    ],
    tips: {
      en: [
        "Lasts only ~20–30 minutes — scout and set up early.",
        "City lights balance perfectly against the deep blue sky.",
        "Use a tripod; exposures are long in this low light.",
        "Slightly cool white balance enhances the blue mood.",
        "Reflections on wet streets and water double the drama.",
      ],
      es: [
        "Dura solo ~20–30 minutos — explora y prepara con antelacion.",
        "Las luces de la ciudad equilibran perfecto contra el cielo azul intenso.",
        "Usa tripode; las exposiciones son largas con esta poca luz.",
        "Un balance de blancos algo frio realza el ambiente azul.",
        "Los reflejos en calles mojadas y agua duplican el drama.",
      ],
    },
    remember: { en: "Short window, tripod, city lights vs blue sky.", es: "Ventana corta, tripode, luces vs cielo azul." },
  },
  {
    id: "long-exposure",
    photo: "/taskcards/long-exposure.jpg",
    icon: "Timer",
    title: { en: "Long Exposure", es: "Exposicion Larga" },
    tagline: { en: "Smooth water, streak clouds, paint with motion and time.", es: "Suaviza el agua, estira nubes, pinta con movimiento y tiempo." },
    settings: [
      { k: "aperture", v: "f/8–f/16" },
      { k: "shutter", v: "1s–several minutes" },
      { k: "iso", v: "100" },
    ],
    tips: {
      en: [
        "A sturdy tripod is non-negotiable.",
        "Use an ND (neutral density) filter to allow long shutters in daylight.",
        "Silky water needs 1–5s; streaking clouds need 30s+.",
        "Trigger with a remote or 2s timer to avoid shake.",
        "Keep ISO at 100 for the cleanest possible file.",
      ],
      es: [
        "Un tripode firme es innegociable.",
        "Usa un filtro ND (densidad neutra) para permitir velocidades lentas de dia.",
        "El agua sedosa necesita 1–5s; las nubes en estela necesitan 30s+.",
        "Dispara con remoto o temporizador de 2s para evitar trepidacion.",
        "Manten el ISO en 100 para el archivo mas limpio posible.",
      ],
    },
    remember: { en: "Tripod + ND filter + patience = silky motion.", es: "Tripode + filtro ND + paciencia = movimiento sedoso." },
  },
  {
    id: "lens-aberrations",
    photo: "/taskcards/lens-aberrations.jpg",
    icon: "Aperture",
    title: { en: "Lens Aberrations", es: "Aberraciones del Objetivo" },
    tagline: { en: "Know your lens's flaws — and when to fix or embrace them.", es: "Conoce los defectos de tu lente — y cuando corregirlos o aprovecharlos." },
    tips: {
      en: [
        "Chromatic aberration: color fringing on high-contrast edges — fix in RAW.",
        "Vignetting: darkened corners — can be corrected or used for mood.",
        "Distortion: barrel (wide) or pincushion (tele) — auto profiles fix it.",
        "Flare & ghosting: from bright lights — use a hood, or embrace for style.",
        "Stopping down 1–2 stops sharpens corners and reduces most aberrations.",
      ],
      es: [
        "Aberracion cromatica: bordes de color en contornos de alto contraste — corrige en RAW.",
        "Vineteo: esquinas oscurecidas — se puede corregir o usar como ambiente.",
        "Distorsion: barril (angular) o cojin (tele) — los perfiles automaticos la corrigen.",
        "Destellos y fantasmas: por luces fuertes — usa parasol o aprovechalos por estilo.",
        "Cerrar 1–2 pasos afina las esquinas y reduce la mayoria de aberraciones.",
      ],
    },
    remember: { en: "Stop down to sharpen; lens profiles fix the rest.", es: "Cierra para afinar; los perfiles corrigen el resto." },
  },
  {
    id: "framing",
    photo: "/taskcards/framing.jpg",
    icon: "Square",
    title: { en: "Framing", es: "Encuadre Natural" },
    tagline: { en: "Use elements in the scene to frame your subject.", es: "Usa elementos de la escena para enmarcar a tu sujeto." },
    tips: {
      en: [
        "Shoot through doorways, windows, arches, branches or tunnels.",
        "A frame adds depth and focuses attention on the subject.",
        "Darker frames around a bright subject create a spotlight effect.",
        "Keep the frame in focus or softly blurred — decide intentionally.",
        "Foreground frames give a sense of place and 3D depth.",
      ],
      es: [
        "Dispara a traves de puertas, ventanas, arcos, ramas o tuneles.",
        "Un marco anade profundidad y enfoca la atencion en el sujeto.",
        "Marcos oscuros alrededor de un sujeto brillante crean un efecto de foco.",
        "Manten el marco enfocado o suavemente desenfocado — decide a proposito.",
        "Los marcos en primer plano dan sensacion de lugar y profundidad 3D.",
      ],
    },
    remember: { en: "Frame the subject with the scene itself.", es: "Enmarca al sujeto con la propia escena." },
  },
  {
    id: "symmetry-patterns",
    photo: "/taskcards/symmetry-patterns.jpg",
    icon: "GridFour",
    title: { en: "Symmetry & Patterns", es: "Simetria y Patrones" },
    tagline: { en: "Repetition and balance create satisfying, graphic images.", es: "La repeticion y el equilibrio crean imagenes graficas y satisfactorias." },
    tips: {
      en: [
        "Center perfectly for formal symmetry (architecture, reflections).",
        "Fill the frame with a repeating pattern for graphic impact.",
        "Break the pattern with one different element for a focal point.",
        "Watch your alignment — symmetry exposes the slightest tilt.",
        "Reflections in water or glass create instant symmetry.",
      ],
      es: [
        "Centra perfectamente para una simetria formal (arquitectura, reflejos).",
        "Llena el encuadre con un patron repetido para impacto grafico.",
        "Rompe el patron con un elemento distinto para crear un punto focal.",
        "Cuida la alineacion — la simetria delata la minima inclinacion.",
        "Los reflejos en agua o vidrio crean simetria al instante.",
      ],
    },
    remember: { en: "Center for symmetry; break the pattern for interest.", es: "Centra para simetria; rompe el patron para interes." },
  },
  {
    id: "negative-space",
    photo: "/taskcards/negative-space.jpg",
    icon: "Square",
    title: { en: "Negative Space", es: "Espacio Negativo" },
    tagline: { en: "Empty space that gives your subject room to breathe.", es: "Espacio vacio que da aire a tu sujeto." },
    tips: {
      en: [
        "Surround a small subject with large, simple empty areas.",
        "Negative space conveys calm, scale, isolation or freedom.",
        "Sky, water, walls and fog make great clean backgrounds.",
        "Leave space in the direction the subject looks or moves.",
        "Don't be afraid of 'emptiness' — it's a powerful design tool.",
      ],
      es: [
        "Rodea a un sujeto pequeno con areas vacias grandes y simples.",
        "El espacio negativo transmite calma, escala, aislamiento o libertad.",
        "Cielo, agua, paredes y niebla hacen fondos limpios excelentes.",
        "Deja espacio en la direccion a la que mira o se mueve el sujeto.",
        "No temas al 'vacio' — es una potente herramienta de diseno.",
      ],
    },
    remember: { en: "Give the subject room to breathe.", es: "Dale aire al sujeto." },
  },
  {
    id: "bokeh",
    photo: "/taskcards/bokeh.jpg",
    icon: "Circle",
    title: { en: "Bokeh", es: "Bokeh" },
    tagline: { en: "The quality of the out-of-focus background blur.", es: "La calidad del desenfoque del fondo." },
    settings: [
      { k: "aperture", v: "f/1.4–f/2.8" },
      { k: "focal", v: "85mm+ helps" },
      { k: "tip", v: "subject far from background" },
    ],
    tips: {
      en: [
        "Wide aperture + long lens + close subject = creamiest bokeh.",
        "Distant background lights become beautiful round orbs.",
        "Put lots of distance between subject and background.",
        "Prime lenses (f/1.8, f/1.4) give the smoothest blur.",
        "Backlight or fairy lights behind the subject create magical bokeh balls.",
      ],
      es: [
        "Apertura amplia + lente largo + sujeto cerca = bokeh mas cremoso.",
        "Las luces lejanas del fondo se vuelven hermosos circulos.",
        "Pon mucha distancia entre el sujeto y el fondo.",
        "Los lentes fijos (f/1.8, f/1.4) dan el desenfoque mas suave.",
        "Contraluz o luces de hada detras del sujeto crean bolas de bokeh magicas.",
      ],
    },
    remember: { en: "Wide aperture + distance = dreamy background.", es: "Apertura amplia + distancia = fondo de ensueno." },
  },
  {
    id: "panning",
    photo: "/taskcards/panning.jpg",
    icon: "ArrowsHorizontal",
    title: { en: "Panning", es: "Barrido (Panning)" },
    tagline: { en: "Track a moving subject for a sharp subject, blurred background.", es: "Sigue a un sujeto en movimiento para sujeto nitido y fondo difuminado." },
    settings: [
      { k: "shutter", v: "1/30–1/125s" },
      { k: "focus", v: "AF-C tracking" },
      { k: "mode", v: "S / Tv (Shutter Priority)" },
    ],
    tips: {
      en: [
        "Use a slower shutter (1/30–1/125s) to blur the background.",
        "Move the camera smoothly with the subject — follow through after the click.",
        "Keep the subject in the same spot of the frame as you pan.",
        "Pan parallel to the motion (a passing car, runner, cyclist).",
        "It takes practice — shoot many; keep the sharp keepers.",
      ],
      es: [
        "Usa una velocidad lenta (1/30–1/125s) para difuminar el fondo.",
        "Mueve la camara suavemente con el sujeto — acompana despues del disparo.",
        "Manten al sujeto en el mismo punto del encuadre mientras barres.",
        "Barre paralelo al movimiento (un auto, corredor, ciclista que pasa).",
        "Requiere practica — dispara muchas; quedate con las nitidas.",
      ],
    },
    remember: { en: "Slow shutter, smooth follow, sharp subject.", es: "Velocidad lenta, seguimiento suave, sujeto nitido." },
  },
  {
    id: "silhouettes",
    photo: "/taskcards/silhouettes.jpg",
    icon: "PersonSimple",
    title: { en: "Silhouettes", es: "Siluetas" },
    tagline: { en: "Expose for the bright background to render the subject black.", es: "Expon para el fondo brillante para volver negro al sujeto." },
    settings: [
      { k: "metering", v: "Spot on the sky" },
      { k: "tip", v: "subject between you & light" },
      { k: "wb", v: "Daylight for warm skies" },
    ],
    tips: {
      en: [
        "Place your subject in front of a bright source (sunset, window).",
        "Meter for the bright background so the subject goes dark.",
        "Turn off the flash — you want the subject unlit.",
        "Choose subjects with a clear, recognizable outline.",
        "Keep subjects separated so shapes don't merge into a blob.",
      ],
      es: [
        "Coloca a tu sujeto frente a una fuente brillante (atardecer, ventana).",
        "Mide para el fondo brillante para que el sujeto quede oscuro.",
        "Apaga el flash — quieres al sujeto sin iluminar.",
        "Elige sujetos con un contorno claro y reconocible.",
        "Manten separados a los sujetos para que las formas no se fundan.",
      ],
    },
    remember: { en: "Expose for the light, let the subject go black.", es: "Expon para la luz, deja al sujeto en negro." },
  },
  {
    id: "reflections",
    photo: "/taskcards/reflections.jpg",
    icon: "Drop",
    title: { en: "Reflections", es: "Reflejos" },
    tagline: { en: "Use water, glass and mirrors for symmetry and depth.", es: "Usa agua, vidrio y espejos para simetria y profundidad." },
    tips: {
      en: [
        "Wet streets, puddles, lakes and windows are reflection goldmines.",
        "Get low and close to the surface for a full mirror effect.",
        "Calm water gives sharp reflections; ripples give painterly ones.",
        "A polarizer can either cut or enhance reflections — rotate it.",
        "Reflections double a scene's symmetry and visual interest.",
      ],
      es: [
        "Calles mojadas, charcos, lagos y ventanas son minas de reflejos.",
        "Ponte bajo y cerca de la superficie para un efecto espejo completo.",
        "El agua en calma da reflejos nitidos; las ondas, reflejos pictoricos.",
        "Un polarizador puede reducir o realzar reflejos — giralo.",
        "Los reflejos duplican la simetria y el interes visual de una escena.",
      ],
    },
    remember: { en: "Get low; calm water mirrors best.", es: "Ponte bajo; el agua en calma refleja mejor." },
  },
];
